import { useCallback } from 'react';
import { useLocation } from 'react-router-dom-v5-compat';

import { CONFIRM_POPUP_MODE, useConfirmPopup } from 'src/components/ConfirmPopup';
import { useHistory } from 'src/lib/hooks';
import { ROUTES } from 'src/routes';
import {
  RESTRICT_ACTION_TYPE,
  useBrandVoices,
  useErrorHandler,
  useRestrictActionPopup,
} from 'src/services/index';
import { useBrandVoicesActions, useCustomerActions, useNotificationActions } from 'src/store';

export const useDeleteBrandVoice = () => {
  const confirmPopup = useConfirmPopup();
  const history = useHistory();
  const location = useLocation();
  const customerActions = useCustomerActions();
  const brandVoicesActions = useBrandVoicesActions();
  const { brandVoices } = useBrandVoices();
  const notificationActions = useNotificationActions();
  const handleError = useErrorHandler();
  const openRestrictActionPopup = useRestrictActionPopup();

  return useCallback(
    (brandVoiceId, brandVoiceName) =>
      new Promise((resolve) => {
        const onConfirm = () => {
          brandVoicesActions
            .deleteBrandVoice(brandVoiceId)
            .then(() => {
              notificationActions.displaySuccess(`Brand voice '${brandVoiceName}' deleted`);
              if (location.pathname.startsWith(`${ROUTES.BRAND_VOICE}/${brandVoiceId}`)) {
                history.push(ROUTES.ROOT);
              }
              resolve(true);
              customerActions.refreshCurrentCustomerFeatureLimit();
            })
            .catch((error) => {
              handleError(error);
              resolve(false);
            });
        };

        const onCancel = () => {
          resolve(false);
        };

        if (brandVoices.filter((item) => item.id !== brandVoiceId).length === 0) {
          openRestrictActionPopup({ actionType: RESTRICT_ACTION_TYPE.deleteLastBrandVoice });
          return;
        }

        confirmPopup({
          mode: CONFIRM_POPUP_MODE.warning,
          title: `Are you sure you want to delete this brand voice?`,
          content:
            'This will permanently remove all the assets in this brand voice, including: tone of voices, brand terms, target audiences, messaging bank assets, custom models, saved prompts and formulas.',
          buttons: {
            confirm: 'Delete brand voice',
            cancel: 'Cancel',
          },
          onConfirm,
          onCancel,
        });
      }),
    [
      brandVoices,
      confirmPopup,
      brandVoicesActions,
      notificationActions,
      location.pathname,
      customerActions,
      history,
      handleError,
      openRestrictActionPopup,
    ]
  );
};
